import React, {FC} from 'react';
import {cx} from './cx';

interface ProgressBarProps {
  value: number;
  max?: number;
  label?: string;
  showValue?: boolean;
  tone?: 'terracotta' | 'leaf' | 'sky' | 'mustard';
  className?: string;
}

const percent = (value: number, max: number) =>
  Math.max(0, Math.min(100, Math.round((value / Math.max(max, 1)) * 100)));

export const ProgressBar: FC<ProgressBarProps> = ({
  value,
  max = 100,
  label,
  showValue = true,
  tone = 'leaf',
  className,
}) => {
  const progress = percent(value, max);
  return (
    <div className={cx('sui-progress', className)}>
      {(label || showValue) && (
        <div className="sui-progress__meta">
          <span>{label}</span>
          {showValue && <strong>{progress}%</strong>}
        </div>
      )}
      <div
        aria-label={label}
        aria-valuemax={max}
        aria-valuemin={0}
        aria-valuenow={value}
        className="sui-progress__track"
        role="progressbar"
      >
        <span
          className={cx(
            'sui-progress__fill',
            `sui-progress__fill--${tone}`
          )}
          style={{transform: `scaleX(${progress / 100})`}}
        />
      </div>
    </div>
  );
};

interface ProgressRingProps {
  value: number;
  max?: number;
  size?: number;
  label?: string;
  tone?: 'terracotta' | 'leaf' | 'sky' | 'mustard';
  className?: string;
}

export const ProgressRing: FC<ProgressRingProps> = ({
  value,
  max = 100,
  size = 112,
  label,
  tone = 'leaf',
  className,
}) => {
  const progress = percent(value, max);
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div
      aria-label={label || `${progress}%`}
      className={cx(
        'sui-progress-ring',
        `sui-progress-ring--${tone}`,
        className
      )}
      role="img"
      style={{height: size, width: size}}
    >
      <svg aria-hidden="true" viewBox="0 0 100 100">
        <circle className="sui-progress-ring__track" cx="50" cy="50" r={radius} />
        <circle
          className="sui-progress-ring__value"
          cx="50"
          cy="50"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <span>
        <strong>{progress}%</strong>
        {label && <small>{label}</small>}
      </span>
    </div>
  );
};
